import { useState, useEffect, useRef } from 'react';
import API, { fmt } from '@/lib/api';
import { ChartBar, Users, Receipt, CashRegister, ArrowUp, ArrowDown, DownloadSimple } from '@phosphor-icons/react';

export default function RapportagePage() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef();

  useEffect(() => {
    API.get('/rapportage').then(r => setData(r.data)).catch(() => {});
  }, []);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { default: html2pdf } = await import('html2pdf.js');
      await html2pdf().set({
        margin: 10,
        filename: `rapportage-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }).from(reportRef.current).save();
    } catch (e) { alert('Export mislukt'); }
    setExporting(false);
  };

  if (!data) return <div className="text-gray-400">Laden...</div>;

  const maanden = Object.entries(data.omzet_maand || {});
  const maxOmzet = Math.max(...maanden.map(([, v]) => v), 1);

  return (
    <div data-testid="rapportage-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="page-heading mb-0">Rapportage & Statistieken</h1>
        <button className="btn btn-primary" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
          <DownloadSimple size={14} weight="bold" /> {exporting ? 'Exporteren...' : 'Exporteer als PDF'}
        </button>
      </div>

      <div ref={reportRef}>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card" style={{ borderLeft: '4px solid #059669' }}>
          <div className="kpi-label">Omzet betaald</div>
          <div className="kpi-value text-[#059669]">{fmt(data.totaal_betaald)}</div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #D97706' }}>
          <div className="kpi-label">Openstaand</div>
          <div className="kpi-value text-[#D97706]">{fmt(data.totaal_open)}</div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #7C3AED' }}>
          <div className="kpi-label">Offertes goedgekeurd</div>
          <div className="kpi-value text-[#7C3AED]">{fmt(data.totaal_offertes)}</div>
        </div>
        <div className="kpi-card" style={{ borderLeft: '4px solid #002FA7' }}>
          <div className="kpi-label">Kassa totaal</div>
          <div className="kpi-value text-[#002FA7]">{fmt(data.kassa?.totaal)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Omzet per maand - bar chart */}
        <div className="section-card">
          <div className="section-header">
            <span className="section-title"><ChartBar size={16} className="inline mr-1" />Omzet per maand</span>
          </div>
          <div className="p-5">
            {maanden.length > 0 ? (
              <div className="space-y-2">
                {maanden.map(([m, v]) => (
                  <div key={m} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-16 text-right" style={{ fontFamily: 'var(--font-mono)' }}>{m}</span>
                    <div className="flex-1 h-6 bg-gray-100 relative">
                      <div className="h-6 transition-all" style={{ width: `${(v / maxOmzet) * 100}%`, background: 'linear-gradient(90deg, #002FA7, #3B82F6)' }} />
                    </div>
                    <span className="text-xs font-bold w-20 text-right">{fmt(v)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">Nog geen omzetdata</div>
            )}
          </div>
        </div>

        {/* Top klanten */}
        <div className="section-card">
          <div className="section-header">
            <span className="section-title"><Users size={16} className="inline mr-1" />Top klanten</span>
          </div>
          <div className="section-body">
            <table className="data-table">
              <thead><tr><th>#</th><th>Klant</th><th className="right">Omzet</th></tr></thead>
              <tbody>
                {(data.top_klanten || []).map((k, i) => (
                  <tr key={k.klant_id}>
                    <td className="font-bold text-[#002FA7]">{i + 1}</td>
                    <td className="font-medium">{k.naam}</td>
                    <td className="right font-bold">{fmt(k.omzet)}</td>
                  </tr>
                ))}
                {(!data.top_klanten || data.top_klanten.length === 0) && (
                  <tr><td colSpan="3" className="text-center text-gray-400 py-6">Geen data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* BTW overzicht */}
        <div className="section-card">
          <div className="section-header"><span className="section-title">BTW overzicht</span></div>
          <div className="p-5 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 flex items-center gap-1"><ArrowUp size={14} className="text-[#DC2626]" />BTW verschuldigd</span>
              <span className="font-bold text-[#DC2626]">{fmt(data.btw_uit)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 flex items-center gap-1"><ArrowDown size={14} className="text-[#059669]" />BTW aftrekbaar</span>
              <span className="font-bold text-[#059669]">{fmt(data.btw_in)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t-2 border-[#002FA7]">
              <span className="font-bold text-[#002FA7]">Saldo te betalen</span>
              <span className="font-bold text-lg text-[#002FA7]">{fmt(data.btw_saldo)}</span>
            </div>
          </div>
        </div>

        {/* Kassa overzicht */}
        <div className="section-card">
          <div className="section-header"><span className="section-title"><CashRegister size={16} className="inline mr-1" />Kassa</span></div>
          <div className="p-5 space-y-3">
            <div className="flex justify-between"><span className="text-sm text-gray-500">Aantal verkopen</span><span className="font-bold">{data.kassa?.aantal || 0}</span></div>
            <div className="flex justify-between"><span className="text-sm text-gray-500">Contant</span><span className="font-bold">{fmt(data.kassa?.contant)}</span></div>
            <div className="flex justify-between"><span className="text-sm text-gray-500">Bancontact</span><span className="font-bold">{fmt(data.kassa?.bancontact)}</span></div>
            <div className="flex justify-between pt-2 border-t-2 border-[#002FA7]"><span className="font-bold text-[#002FA7]">Totaal</span><span className="font-bold text-lg text-[#002FA7]">{fmt(data.kassa?.totaal)}</span></div>
          </div>
        </div>

        {/* Document aantallen */}
        <div className="section-card">
          <div className="section-header"><span className="section-title"><Receipt size={16} className="inline mr-1" />Documenten</span></div>
          <div className="p-5 space-y-2">
            {Object.entries(data.doc_counts || {}).map(([type, count]) => {
              const labels = { factuur: 'Facturen', offerte: 'Offertes', bestelbon: 'Bestelbonnen', leverbon: 'Leveringsbonnen', proforma: 'Proforma', creditnota: "Creditnota's" };
              const colors = { factuur: '#002FA7', offerte: '#7C3AED', bestelbon: '#2563EB', leverbon: '#059669', proforma: '#D97706', creditnota: '#DC2626' };
              return (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm flex items-center gap-2">
                    <span className="w-2.5 h-2.5 inline-block" style={{ background: colors[type] || '#6B7280' }} />
                    {labels[type] || type}
                  </span>
                  <span className="font-bold">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </div>{/* end reportRef */}
    </div>
  );
}
